import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, Button, IconButton, Typography } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import StarsIcon from '@material-ui/icons/Stars';
import {
  TEST_ATTEMPT_ROUTE,
  TEST_ATTEMPT_PREVIEW_ROUTE,
  TEST_PREPARATION_MODE_ROUTE,
  SUBSCRIPTION_ROUTE,
} from '../../constants/route.constant';
import { authService, axiosService } from '../../services';

import './premiumOfferDialog.scss';

// Post-login promo announcing the user's free metered Premium attempts
// (backend: premium-attempts-status). Shown while exploring, at most once per
// 24h and at most MAX_TOTAL_SHOWS times overall; it stops naturally once the
// pool is spent (remaining 0 → never shows) or the user subscribes.
// Mirrors AppPromoDialog's safety rules: never on test routes, never over
// another open dialog.

const LAST_SHOWN_KEY = 'premium_offer_last_shown';
const SHOW_COUNT_KEY = 'premium_offer_show_count';

const COOLDOWN_MS = 24 * 60 * 60_000;
const MAX_TOTAL_SHOWS = 5;

// Give the user a moment to land before pitching; retry while something
// blocks (other dialog open, excluded route).
const INITIAL_DELAY_MS = 6_000;
const RETRY_MS = 5_000;

const prefixOf = (path: string) => path.split('/:')[0];
const EXCLUDED_PREFIXES = [
  prefixOf(TEST_ATTEMPT_ROUTE.path),
  prefixOf(TEST_ATTEMPT_PREVIEW_ROUTE.path),
  prefixOf(TEST_PREPARATION_MODE_ROUTE.path),
  prefixOf(SUBSCRIPTION_ROUTE.path),
];

const readNumber = (key: string) => {
  try {
    return parseInt(localStorage.getItem(key) || '0', 10) || 0;
  } catch {
    return 0;
  }
};

const writeNumber = (key: string, n: number) => {
  try {
    localStorage.setItem(key, String(n));
  } catch {
    // ignore storage failures (private mode etc.)
  }
};

const onExcludedRoute = () => EXCLUDED_PREFIXES.some((p) => window.location.pathname.startsWith(p));
const otherDialogOpen = () => document.querySelectorAll('.MuiDialog-root:not(.premium-offer-dialog)').length > 0;

export const PremiumOfferDialog = () => {
  const [open, setOpen] = useState(false);
  const [remaining, setRemaining] = useState(0);

  useEffect(() => {
    let loggedIn = false;
    try {
      loggedIn = authService.isLoggedIn;
    } catch {
      loggedIn = false;
    }
    if (!loggedIn) return;

    if (readNumber(SHOW_COUNT_KEY) >= MAX_TOTAL_SHOWS) return;
    if (Date.now() - readNumber(LAST_SHOWN_KEY) < COOLDOWN_MS) return;

    let cancelled = false;
    let timerId: number;

    const tryShow = async () => {
      if (cancelled) return;
      if (onExcludedRoute() || otherDialogOpen()) {
        timerId = window.setTimeout(tryShow, RETRY_MS);
        return;
      }

      try {
        const { data: { data: status } } = await axiosService.setAuth(true).get('assessment/test-attempt/premium-attempts-status');
        // Nothing to pitch: subscribers are unlimited, and an empty pool is
        // handled contextually by the paywall dialogs instead.
        if (cancelled || status.isPremiumUser || status.remaining <= 0) return;

        setRemaining(status.remaining);
        setOpen(true);
        writeNumber(LAST_SHOWN_KEY, Date.now());
        writeNumber(SHOW_COUNT_KEY, readNumber(SHOW_COUNT_KEY) + 1);
      } catch {
        // Status endpoint unreachable — skip quietly, never block the app.
      }
    };

    timerId = window.setTimeout(tryShow, INITIAL_DELAY_MS);

    return () => {
      cancelled = true;
      clearTimeout(timerId);
    };
  }, []);

  const dismiss = () => setOpen(false);

  return (
    <Dialog open={open} onClose={dismiss} className="premium-offer-dialog" aria-labelledby="premium-offer-title">
      <DialogContent className="premium-offer-content">
        <IconButton className="premium-offer-close" size="small" aria-label="close" onClick={dismiss}>
          <CloseIcon fontSize="inherit" />
        </IconButton>

        <div className="premium-offer-banner">
          <StarsIcon className="premium-offer-icon" />
          <span className="premium-offer-badge">FREE ACCESS</span>
        </div>

        <Typography component="h2" id="premium-offer-title" className="premium-offer-title">
          You have {remaining} free Premium {remaining === 1 ? 'attempt' : 'attempts'}!
        </Typography>
        <Typography component="p" className="premium-offer-subtitle">
          Experience Premium mock tests with real exam patterns, detailed
          solutions and performance analytics. On us, no payment needed.
        </Typography>

        <Button
          href="/mock-tests"
          variant="contained"
          size="large"
          className="premium-offer-cta"
          onClick={dismiss}
        >
          Try a Premium Test Now
        </Button>

        <Button className="premium-offer-dismiss" size="small" onClick={dismiss}>
          Maybe later
        </Button>
      </DialogContent>
    </Dialog>
  );
};
